import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ApimHandler } from '@api/apim-handler';
import { errorAlert } from '@components/toastify/notify-toast';
import { ApiEndpoint, ApiGroup } from '@pages/api-portal/types';
import { filterCatalogEndpoints, groupEndpointsByGroup, resolveCatalogGroupParam } from '@pages/api-portal/utils';

/*
 * Owns catalog data fetching, URL-driven group selection (?group=), local filter state,
 * and derived groupings for the Catalog browse page.
 */
export function useCatalog() {
    const history = useHistory();
    const location = useLocation();
    const initialGroup = new URLSearchParams(location.search).get('group');

    const [apiGroups, setApiGroups] = useState<ApiGroup[]>([]);
    const [allEndpoints, setAllEndpoints] = useState<ApiEndpoint[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [catalogFetchFailed, setCatalogFetchFailed] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
    const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<string | null>(initialGroup);
    const [expandedGroups, setExpandedGroups] = useState<string[]>(initialGroup ? [initialGroup] : []);

    /*
     * Loads API groups and all endpoints for the catalog.
     */
    const fetchCatalog = useCallback(async () => {
        setIsLoading(true);
        setCatalogFetchFailed(false);
        try {
            const [groupsRes, endpointsRes] = await Promise.all([
                ApimHandler.fetchApiGroups(),
                ApimHandler.fetchAllEndpoints(),
            ]);
            setApiGroups(groupsRes.data);
            setAllEndpoints(endpointsRes.data);
        } catch {
            setCatalogFetchFailed(true);
            errorAlert('Failed to load the API catalog.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCatalog();
    }, [fetchCatalog]);

    // Seed expanded groups once catalog data arrives, unless a ?group= param already narrowed it.
    useEffect(() => {
        if (expandedGroups.length > 0) return;
        if (apiGroups.length > 0) {
            setExpandedGroups(apiGroups.map((g) => resolveCatalogGroupParam(g, allEndpoints)));
        } else if (allEndpoints.length > 0) {
            setExpandedGroups(Array.from(new Set(allEndpoints.map((e) => String(e.group)))));
        }
    }, [apiGroups, allEndpoints]);

    /*
     * Applies a group filter and reflects the selection in the URL's ?group= param.
     */
    const applyGroupFilter = useCallback(
        (next: string | null) => {
            setSelectedGroup(next);
            const params = new URLSearchParams(location.search);
            if (next) {
                params.set('group', next);
                setExpandedGroups((prev) => (prev.includes(next) ? prev : [...prev, next]));
            } else {
                params.delete('group');
            }
            const qs = params.toString();
            history.replace(qs ? `${location.pathname}?${qs}` : location.pathname);
        },
        [history, location.pathname, location.search],
    );

    const filteredEndpoints = useMemo(
        () =>
            filterCatalogEndpoints(allEndpoints, {
                searchQuery,
                selectedGroup,
                selectedMethods,
                selectedVersions,
                selectedStatuses,
            }),
        [allEndpoints, searchQuery, selectedGroup, selectedMethods, selectedVersions, selectedStatuses],
    );

    const groupedEndpoints = useMemo(() => groupEndpointsByGroup(filteredEndpoints), [filteredEndpoints]);

    /*
     * Toggles a group's expanded state in the accordion list.
     */
    const toggleGroup = useCallback((groupId: string) => {
        setExpandedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    }, []);

    /*
     * Toggles a value inside a multi-select filter array (method/version/status).
     */
    const toggleFilterOption = useCallback(
        (value: string, selected: string[], setSelected: (next: string[]) => void) => {
            setSelected(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
        },
        [],
    );

    /*
     * Clears every active filter, including the group selection and its URL param.
     */
    const clearFilters = useCallback(() => {
        setSearchQuery('');
        setSelectedMethods([]);
        setSelectedVersions([]);
        setSelectedStatuses([]);
        applyGroupFilter(null);
    }, [applyGroupFilter]);

    const hasActiveFilters = Boolean(
        searchQuery || selectedMethods.length > 0 || selectedVersions.length > 0 || selectedStatuses.length > 0 || selectedGroup,
    );

    return {
        apiGroups,
        allEndpoints,
        isLoading,
        catalogFetchFailed,
        retryCatalogFetch: fetchCatalog,
        searchQuery,
        setSearchQuery,
        selectedGroup,
        applyGroupFilter,
        selectedMethods,
        setSelectedMethods,
        selectedVersions,
        setSelectedVersions,
        selectedStatuses,
        setSelectedStatuses,
        expandedGroups,
        toggleGroup,
        toggleFilterOption,
        clearFilters,
        hasActiveFilters,
        filteredEndpoints,
        groupedEndpoints,
    };
}
